// components/dashboard/DashboardComponents.js
import React from 'react';
import { 
  FileText, 
  Clock, 
  TrendingUp, 
  Eye, 
  Mail, 
  Download, 
  Play, 
  Pause, 
  Edit2, 
  Trash2, 
  Plus,
  RefreshCw,
  BarChart3
} from 'lucide-react';
import Button from '../../Button/Button';

export const DashboardHeader = ({ onCreateReport, onRefresh, lastRefresh, loading }) => (
  <div className="dashboard-header">
    <div className="header-content">
      <div className="header-title-section">
        <div>
          <h1 className="dashboard-title">Reporting Dashboard</h1>
          <p className="dashboard-subtitle">Analytics, scheduling, and report management</p>
        </div>
        <div className="header-status">
          <div className="live-indicator">
            <div className="live-dot"></div>
            Live Data
          </div>
          <Button
            variant="ghost"
            size="small"
            icon={<RefreshCw size={12} className={loading ? 'animate-spin' : ''} />}
            onClick={onRefresh}
            disabled={loading}
            className="refresh-button"
          >
            Last update: {lastRefresh.toLocaleTimeString()}
          </Button>
        </div>
      </div>
      <div className="header-actions">
        <Button className="create-button" onClick={onCreateReport}>
          <Plus size={16} />
          <span className="create-button-text">Create Report</span>
        </Button>
      </div>
    </div>
  </div>
);

export const StatsGrid = ({ stats }) => (
  <div className="stats-section">
    <div className="stats-grid">
      <div className="stat-card">
        <div className="stat-content">
          <div className="stat-icon-wrapper stat-icon-blue">
            <FileText className="stat-icon" />
          </div>
          <div className="stat-info">
            <p className="stat-label">Active Reports</p>
            <p className="stat-value">{stats.active_reports}</p>
          </div>
        </div>
      </div>
      
      <div className="stat-card">
        <div className="stat-content">
          <div className="stat-icon-wrapper stat-icon-green">
            <Clock className="stat-icon" />
          </div>
          <div className="stat-info">
            <p className="stat-label">Scheduled</p>
            <p className="stat-value">{stats.total_scheduled}</p>
          </div>
        </div>
      </div>
      
      <div className="stat-card">
        <div className="stat-content">
          <div className="stat-icon-wrapper stat-icon-orange">
            <TrendingUp className="stat-icon" />
          </div>
          <div className="stat-info">
            <p className="stat-label">This Week</p>
            <p className="stat-value">{stats.reports_this_week}</p>
          </div>
        </div>
      </div>
      
      <div className="stat-card">
        <div className="stat-content">
          <div className="stat-icon-wrapper stat-icon-purple">
            <Eye className="stat-icon" />
          </div>
          <div className="stat-info">
            <p className="stat-label">Total Views</p>
            <p className="stat-value">{stats.total_views >= 1000 ? `${(stats.total_views / 1000).toFixed(1)}k` : stats.total_views}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export const ScheduledReportsSection = ({ 
  reports, 
  onCreateReport, 
  onToggleReport, 
  onEditReport, 
  onDeleteReport, 
  onDownloadReport, 
  formatSchedule, 
  loading 
}) => (
  <div className="content-section scheduled-reports">
    <div className="section-header">
      <div className="section-title">
        <Clock className="section-icon" />
        Scheduled Reports
      </div>
      <Button
        variant="outline"
        style={{ width: 'auto' }}
        size="small"
        onClick={onCreateReport}
        icon={<Plus size={16} />}
        className="section-action"
      >
        <span className="section-action-text">Add</span>
      </Button>
    </div>

    <div className="reports-list">
      {reports.length === 0 ? (
        <div className="empty-state">
          <FileText className="empty-state-icon" />
          <h3>No scheduled reports</h3>
          <p>Create your first scheduled report to get started</p>
        </div>
      ) : (
        reports.map((report) => (
          <div key={report.id} className="report-item">
            <div className="report-content">
              <div className="report-icon-wrapper">
                <div className="report-icon-bg">
                  <FileText className="report-icon" />
                </div>
              </div>
              <div className="report-details">
                <div className="report-header">
                  <h3 className="report-name">{report.name}</h3>
                  <span className={`status-badge ${report.status === 'active' ? 'status-active' : 'status-paused'}`}>
                    {report.status}
                  </span>
                </div>
                <p className="report-meta">
                  {report.report_type} • {formatSchedule(report.frequency, report.frequency_config)}
                </p>
                <div className="report-stats">
                  <span className="report-stat">
                    <Mail size={12} />
                    {report.recipient_count || 0} recipients
                  </span>
                </div>
              </div>
            </div>
            <div className="report-actions">
              <button 
                className="action-button action-download"
                onClick={() => onDownloadReport(report)}
                title="Generate and Download"
                disabled={loading}
              >
                <Download size={14} />
              </button>
              
              <button 
                className="action-button action-play"
                onClick={() => onToggleReport(report.id, report.status)}
                title={report.status === 'active' ? 'Pause' : 'Resume'}
              >
                {report.status === 'active' ? <Pause size={14} /> : <Play size={14} />}
              </button>
              
              <button 
                className="action-button action-edit"
                onClick={() => onEditReport(report)}
                title="Edit"
              >
                <Edit2 size={14} />
              </button>
              
              <button 
                className="action-button action-delete"
                onClick={() => onDeleteReport(report)}
                title="Delete"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  </div>
);

export const RecentActivitySection = ({ 
  activity, 
  onViewReport, 
  onDownloadReport 
}) => (
  <div className="content-section recent-activity">
    <div className="section-header">
      <div className="section-title">
        <BarChart3 className="section-icon" />
        Recent Activity
      </div>
    </div>

    <div className="activity-list">
      {activity.length === 0 ? (
        <div className="empty-state">
          <BarChart3 className="empty-state-icon" />
          <h3>No recent activity</h3>
          <p>Report executions will appear here</p>
        </div>
      ) : (
        activity.map((item, index) => (
          <div key={item.id || index} className="activity-item">
            <div className={`activity-status ${
              item.status === 'completed' ? 'status-delivered' : 
              item.status === 'processing' ? 'status-processing' : 
              'status-default'
            }`}></div>
            <div className="activity-content">
              <div className="activity-icon-wrapper">
                <FileText className="activity-icon" />
              </div>
              <div className="activity-details">
                <h3 
                  className="activity-name"
                  onClick={() => onViewReport(item.id)}
                  style={{ cursor: 'pointer' }}
                >
                  {item.name}
                </h3>
                <div className="activity-meta">
                  <span>{new Date(item.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
            <div className="activity-actions">
              <span className="format-badge">{item.output_format?.toUpperCase() || 'PDF'}</span>
              {item.status === 'completed' && (
                <button
                  className="download-button"
                  onClick={() => {
                    const fileExtension = item.output_format?.toLowerCase() || 'pdf';
                    onDownloadReport(item, `${item.name}.${fileExtension}`);
                  }}
                  title="Download"
                >
                  <Download size={12} />
                </button>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  </div>
);

export const ErrorBanner = ({ error, onDismiss }) => {
  if (!error) return null;
  
  return (
    <div className="error-banner">
      <p>{error}</p>
      <Button
        variant="ghost"
        size="small"
        style={{ width: 'auto' }}
        onClick={onDismiss}
      >
        Dismiss
      </Button>
    </div>
  );
};

export const LoadingContainer = () => (
  <div className="loading-container">
    <div className="loading-spinner"></div>
    <p>Loading dashboard...</p>
  </div>
);